import React from 'react';
import { CircularProgress, Alert, Box } from '@mui/material';
import { Entity } from '@backstage/catalog-model';
import { useProjectCode } from './useProjectCode';
import { ProjectCodeWrapper } from './ProjectCodeWrapper';
import { RefreshProvider } from './RefreshContext';
import { CloudProviderSection } from './CloudProviderSection';
import { AwsWithProviders } from './Aws';
import { KubernetesWithProviders } from './Kubernetes';
import { AzureWithProviders } from './Azure';
import { GcpWithProviders } from './Gcp';

type CrdPageProps = {
  entity: Entity;
};

export const CrdPage: React.FC<CrdPageProps> = ({ entity }) => {
  const { value: projectCodeData, loading, error } = useProjectCode(entity);

  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load project code. Please try again later.
      </Alert>
    );
  }

  if (!projectCodeData || !projectCodeData.project_code) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No project code available.
      </Alert>
    );
  }

  const projectCode = projectCodeData.project_code;
  const awsConfigs = projectCodeData?.aws ?? [];
  const kubernetesConfigs = projectCodeData?.kubernetes ?? [];
  const azureConfigs = projectCodeData?.azure ?? [];
  const gcpConfigs = projectCodeData?.gcp ?? [];

  return (
    <RefreshProvider>
      <ProjectCodeWrapper
        projectCode={projectCode}
        awsConfigs={awsConfigs}
        gcpConfigs={gcpConfigs}
        azureConfigs={azureConfigs}
        kubernetesConfigs={kubernetesConfigs}
      >
        <Box sx={{ mt: 2 }}>
          {/* AWS Section */}
          <CloudProviderSection
            title="Amazon Web Services (AWS)"
            configCount={awsConfigs.length}
            color="#FF9900"
          >
            <AwsWithProviders
              projectCode={projectCode}
              awsConfigs={awsConfigs}
            />
          </CloudProviderSection>

          {/* Azure Section */}
          <CloudProviderSection
            title="Microsoft Azure"
            configCount={azureConfigs.length}
            color="#0078D4"
          >
            <AzureWithProviders
              projectCode={projectCode}
              azureConfigs={azureConfigs}
            />
          </CloudProviderSection>

          {/* GCP Section */}
          <CloudProviderSection
            title="Google Cloud Platform (GCP)"
            configCount={gcpConfigs.length}
            color="#D32F2F"
          >
            <GcpWithProviders
              projectCode={projectCode}
              gcpConfigs={gcpConfigs}
            />
          </CloudProviderSection>

          {/* Kubernetes Section */}
          <CloudProviderSection
            title="Kubernetes"
            configCount={kubernetesConfigs.length}
            color="#326CE5"
          >
            <KubernetesWithProviders
              projectCode={projectCode}
              kubernetesConfigs={kubernetesConfigs}
            />
          </CloudProviderSection>
        </Box>
      </ProjectCodeWrapper>
    </RefreshProvider>
  );
};
